// Cursor tooltips share the Pixi overlay with context menus and held stacks.
import { api } from '../api';
import { Widget, widget, activeRoot, uiScale, itemIcon } from './compose';
export interface TipLine { t?: string; c?: string; v?: string | null; vc?: string; ch?: string | null; chc?: string | null; gap?: boolean }
/** rarity: an item's quality, right of its name. */
export interface TipData { title: string; color: string; rarity?: string; lines: TipLine[]; worn?: TipData }
export class Tooltip extends Widget {
  constructor(tip: TipData) {
    super('box', { class: 'panel tooltip' });
    this.append(tip.rarity
      ? widget('box', { class: 'tip-line', style: { alignItems: 'center' } },
        widget('text', { class: 'tip-title grow', style: { color: tip.color } }, tip.title),
        widget('text', { class: 'tip-value', style: { color: tip.color, whiteSpace: 'nowrap' } }, tip.rarity))
      : widget('box', { class: 'tip-title', style: { color: tip.color } }, tip.title));
    for (const l of tip.lines) this.append(l.gap ? widget('box', { class: 'tip-gap' }) : widget('box', { class: 'tip-line' },
      widget('text', { class: 'grow', style: { color: l.c } }, l.t ?? ''),
      l.ch ? widget('text', { class: 'tip-value', style: { color: l.chc ?? '', whiteSpace: 'nowrap' } }, l.ch) : null,
      l.v ? widget('text', { class: 'tip-value', style: { color: l.vc ?? l.c, whiteSpace: 'nowrap' } }, l.v) : null));
  }
}
let boxes: Tooltip[] = [], shown = '';
export function hideTip(): void { for (const b of boxes) b.dispose(); boxes = []; shown = ''; }
export function showTip(tip: TipData | null, x: number, y: number, key = ''): void {
  if (!tip?.title || !activeRoot) { hideTip(); return; }
  const k = key || JSON.stringify(tip);
  if (k !== shown) { hideTip(); shown = k; boxes = [new Tooltip(tip)]; if (tip.worn) boxes.push(new Tooltip(tip.worn)); activeRoot.overlay.append(...boxes); }
  const w = innerWidth / uiScale, h = innerHeight / uiScale, gap = 3;
  // On narrow screens comparison panels share the available width.
  for (const box of boxes) box.style.maxWidth = `${Math.min(170, (w - 8 - gap * (boxes.length - 1)) / boxes.length)}px`;
  activeRoot.layout();
  const total = boxes.reduce((n, b) => n + b.boxWidth, 0) + gap * (boxes.length - 1);
  let bx = x / uiScale + 12;
  if (bx + total > w - 2) bx = x / uiScale - 8 - total;
  bx = Math.max(2, bx);
  for (const b of boxes) { b.style.left = `${bx}px`; b.style.top = `${Math.max(2, Math.min(y / uiScale + 8, h - 2 - b.boxHeight))}px`; bx += b.boxWidth + gap; }
}
export function itemTip(item: number, opts: { bound?: boolean; count?: number; hint?: string; compare?: boolean; shift?: boolean }): TipData {
  return api.itemTip(item, !!opts.bound, opts.count ?? 1, opts.hint ?? '', !!opts.compare, !!opts.shift) as TipData;
}
export function simpleTip(title: string, lines: (string | [string, string])[], color = 'var(--accent)'): TipData {
  return { title, color, lines: lines.map(l => typeof l === 'string' ? { t: l, c: 'var(--dim)' } : { t: l[0], c: l[1] }) };
}
const makers = new WeakMap<Widget, () => TipData | null>();
export function hoverTip(w: Widget, make: () => TipData | null): void { makers.set(w, make); }
let lastX = NaN, lastY = NaN, lastLayout = -1, lastCheck = 0;
let hovering: Widget | null = null;
let shiftRevision = 0, lastShift = -1;
for (const type of ['keydown', 'keyup'] as const) window.addEventListener(type, e => { if (e.key === 'Shift') shiftRevision++; });
export function updateTooltip(x: number, y: number): void {
  if (!activeRoot) return;
  activeRoot.layout();
  const moved = x !== lastX || y !== lastY;
  const changed = lastLayout !== activeRoot.layoutRevision;
  if (moved || changed) {
    hovering = activeRoot.hitTest(x, y);
    while (hovering && !makers.has(hovering) && !hovering.title) hovering = hovering.parentWidget;
    lastX = x; lastY = y; lastLayout = activeRoot.layoutRevision;
  }
  if (!hovering) { if (boxes.length) hideTip(); return; }
  const now = performance.now();
  // Dynamic hints/cooldowns can change without layout; poll only the hovered maker.
  if (!moved && !changed && lastShift === shiftRevision && now - lastCheck < 150) return;
  lastCheck = now; lastShift = shiftRevision;
  showTip(makers.get(hovering)?.() ?? (hovering.title ? simpleTip(hovering.title, []) : null), x, y);
}
export { itemIcon };
